import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRFQ } from '../contexts/RFQContext';
import { usePublishRobbieApplied, useRecordRobbieAction } from '../contexts/RobbieUiContext';
import { RobbieProposal } from '../types/robbieProposal';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { applyRobbieProposal } from '../utils/robbieProposalApply';
import { criteriaDraftActions, SavedRequirements } from '../utils/robbieCriteriaDraft';
import { robbieActionText } from '../utils/robbieActivity';

/**
 * One step card's state: apply it (save through the step's own endpoint and
 * update the screen at once), dismiss it, and what became of it.
 *
 * The outcome is kept per message in `localStorage`, like the BOM card, so a
 * reload redraws it as it was left. Storage is a convenience: without it the
 * card opens again, and re-applying sets the same values a second time.
 */
export type RobbieProposalOutcome = { status: 'open' } | { status: 'applied' } | { status: 'dismissed' };

const STORAGE_PREFIX = 'hr.robbie.proposal.';

const readOutcome = (key: string): RobbieProposalOutcome => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_PREFIX + key) || 'null');
    if (parsed && (parsed.status === 'applied' || parsed.status === 'dismissed')) return parsed;
  } catch {
    // Unreadable storage is the same as none.
  }
  return { status: 'open' };
};

const writeOutcome = (key: string, outcome: RobbieProposalOutcome) => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(outcome));
  } catch {
    // Private mode or a full quota: the card still shows the outcome now.
  }
};

const sentence = (text: string) => text.trim().replace(/[.\s]+$/, '');

export const useRobbieProposal = (storageKey: string, proposal: RobbieProposal, rfqId: string | undefined) => {
  const [outcome, setOutcome] = useState<RobbieProposalOutcome>(() => readOutcome(storageKey));
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const { fetchRFQ } = useRFQ();
  const publish = usePublishRobbieApplied();
  const recordAction = useRecordRobbieAction();

  const settle = useCallback((next: RobbieProposalOutcome) => {
    writeOutcome(storageKey, next);
    setOutcome(next);
  }, [storageKey]);

  const apply = useCallback(async () => {
    if (applying) return;
    setApplying(true);
    setError(null);
    try {
      await applyRobbieProposal(proposal, {
        publish,
        refreshRfq: (keepCriteria) => (rfqId ? fetchRFQ(rfqId, true, true, keepCriteria) : Promise.resolve()),
        applyCriteriaToDraft: (changes, saved) => criteriaDraftActions(changes, saved as SavedRequirements).forEach((a) => dispatch(a)),
      });
      settle({ status: 'applied' });
      recordAction('proposalApplied', robbieActionText.appliedStepProposal(proposal.rows.map((r) => r.title)));
    } catch (err) {
      const reason = sentence(apiErrorMessage(err, "the server didn't say why"));
      setError(`I couldn't finish applying this: ${reason}. Check the step, then try again or make the change yourself.`);
    } finally {
      setApplying(false);
    }
  }, [applying, proposal, publish, rfqId, fetchRFQ, dispatch, settle, recordAction]);

  const dismiss = useCallback(() => {
    settle({ status: 'dismissed' });
    recordAction('proposalDismissed', robbieActionText.dismissedProposal());
  }, [settle, recordAction]);

  return { outcome, applying, error, apply, dismiss };
};

export default useRobbieProposal;
